import React, { useState } from 'react';
import { Box, Typography, Menu, Button, Chip, Stack } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AddIcon from '@mui/icons-material/Add';
import { TransportMethod } from '@domain/shipment/models/TransportMethod';
import { ShipmentMethod } from '@domain/shipment/models/ShipmentMethod';
import { useShipmentMethods } from '@features/shipments/hooks/useShipmentMethods';
import CreateShipmentMethodDialog from '@features/shipments/components/methods/CreateShipmentMethodDialog';
import EditShipmentMethodDialog from '@features/shipments/components/methods/EditShipmentMethodDialog';
import { Icon } from '@shared/icons';
import { DurationRow, LeadTimeSummary } from '@features/products/components/leadtime';

interface BulkLeadTimeDropdownProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onValueChange: (field: string, value: any) => void;
  onShipmentMethodChange: (method: ShipmentMethod) => void;
  onConfirm?: () => void;
  isLoading?: boolean;
  values: {
    productionDuration: string;
    processingDuration: string;
    transportMethod: TransportMethod;
    shippingDuration: string;
  };
  selectedShipmentMethod?: ShipmentMethod;
}

const BulkLeadTimeDropdown: React.FC<BulkLeadTimeDropdownProps> = ({
  anchorEl,
  open,
  onClose,
  onValueChange,
  onShipmentMethodChange,
  onConfirm,
  isLoading = false,
  values,
  selectedShipmentMethod
}) => {
  const { shipmentMethods, createShipmentMethod } = useShipmentMethods();
  const [shipmentOptionsRowOpen, setShipmentOptionsRowOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const handleValueChange = (field: string, value: any) => {
    onValueChange(field, value);
  };

  const handleCreateShipmentMethod = async (newMethod: any) => {
    const result = await createShipmentMethod({
      transportMethod: newMethod.transportMethod,
      duration: newMethod.duration,
      name: newMethod.name,
    });

    onShipmentMethodChange(result);
    setCreateDialogOpen(false);
  };

  const handleShipmentMethodChange = (method: ShipmentMethod) => {
    onShipmentMethodChange(method);
    setShipmentOptionsRowOpen(false);
  };

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 0, width: 400 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', px: 3, pt: 2 }}>
            Lead Time
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <DurationRow
              label="Production Time"
              value={values.productionDuration === '' ? undefined : Number(values.productionDuration)}
              onChange={(value) => handleValueChange('productionDuration', value ?? '')}
            />

            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center', px: 3, height: 40 }}>
              <Stack direction="row" gap={1} alignItems="center" justifyContent="flex-start" flex={1}>
                <Typography variant="body2" sx={{ width: '100px' }}>Shipping Time</Typography>

                {selectedShipmentMethod ? (
                  <Chip
                    color="secondary"
                    label={selectedShipmentMethod.name ? `${selectedShipmentMethod.name}` : `${selectedShipmentMethod.transportMethod}`}
                    icon={shipmentOptionsRowOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                    onClick={() => setShipmentOptionsRowOpen(!shipmentOptionsRowOpen)}
                  />
                ) : (
                  <Chip
                    onClick={() => setShipmentOptionsRowOpen(!shipmentOptionsRowOpen)}
                    variant="outlined"
                    label="Add shipment method"
                    color="secondary"
                    sx={{ cursor: 'pointer' }}
                    icon={shipmentOptionsRowOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                  />
                )}
              </Stack>

              {selectedShipmentMethod && (
                <Button
                  onClick={() => setEditDialogOpen(true)}
                  endIcon={<Icon icon="IcEdit" />}
                  sx={{
                    '.edit-icon': {
                      display: 'none',
                    },
                    '&:hover .edit-icon': {
                      display: 'block'
                    }
                  }}
                  variant="text"
                  color="secondary"
                >
                  <Typography variant="body2">
                    {(selectedShipmentMethod?.duration || 0) + ' days'}
                  </Typography>
                </Button>
              )}
            </Box>

            {shipmentOptionsRowOpen && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, px: 3, py: 0 }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', overflowX: 'auto', gap: 1, alignItems: 'center', pb: 2 }}>
                  {shipmentMethods?.map(method => (
                    <Chip
                      key={method.id}
                      onClick={() => handleShipmentMethodChange(method)}
                      label={method.name ? `${method.name} (${method.duration} days)` : `${method.transportMethod} (${method.duration} days)`}
                      color="secondary"
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}

                  <Chip
                    key="default"
                    variant="outlined"
                    icon={<AddIcon fontSize="small" />}
                    onClick={() => setCreateDialogOpen(true)}
                    label="Create New"
                    color="secondary"
                    sx={{ cursor: 'pointer' }}
                  />
                </Box>
              </Box>
            )}

            <DurationRow
              label="Processing Time"
              value={values.processingDuration === '' ? undefined : Number(values.processingDuration)}
              onChange={(value) => handleValueChange('processingDuration', value ?? '')}
            />

            <LeadTimeSummary
              productionDuration={Number(values.productionDuration) || 0}
              shippingDuration={Number(values.shippingDuration) || 0}
              processingDuration={Number(values.processingDuration) || 0}
            />

            {onConfirm && (
              <Box sx={{ p: 3, pt: 0 }}>
                <Button
                  variant="contained"
                  onClick={onConfirm}
                  disabled={isLoading}
                  fullWidth
                >
                  {isLoading ? 'Saving...' : 'Confirm Lead Time'}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Menu>

      <CreateShipmentMethodDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleCreateShipmentMethod}
      />

      <EditShipmentMethodDialog
        shipmentMethod={selectedShipmentMethod}
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onFinish={handleShipmentMethodChange}
      />
    </>
  );
};

export default BulkLeadTimeDropdown;
